import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/ac-smart-voucher-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  ACSmartVoucherModuleSettings,
  BaseModuleProps,
  Language,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<ACSmartVoucherModuleSettings>;

const languageMapping: { [key in Language]?: 1 | 2 | 3 } = {
  de: 1,
  it: 2,
  en: 3,
};

const getIframeUrl = ({
  cssUrl,
  languageNumber,
  kID,
}: {
  cssUrl: string;
  kID: string;
  languageNumber: 1 | 2 | 3;
}) =>
  "https://voucher.ariescreative.com/widget/voucher.php" +
  `?kID=${kID}&lng=${languageNumber}&css=${cssUrl}`;

const ACSmartVoucherModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    translation: {
      languageId,
      settings: { title, subtitle },
    },
    settings: { textAlign, cssUrl, kID },
  },
  translatedModule,
  isFirstOnPage,
  isPreview,
  isActive,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const ref = useRef<HTMLDivElement>(null);
  const supportedLanguage = getSupportedLanguage(languageId, [
    "de",
    "it",
    "en",
  ]);
  const languageNumber = languageMapping[supportedLanguage];

  const requiredFieldsFilled = Boolean(cssUrl && kID);
  const hasTitleOrSubtitle = Boolean(title || subtitle);

  useEffect(() => {
    if (!requiredFieldsFilled) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://voucher.ariescreative.com/widget/js/smartvoucher.js";
    ref.current?.insertAdjacentElement("beforeend", script);
  }, [requiredFieldsFilled, hasTitleOrSubtitle]);

  return (
    <ModuleWithHeadings
      className={styles.ACSmartVoucherModule}
      id={moduleId}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
      title={title}
      subtitle={subtitle}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>
      <div
        className={ClassNames(
          styles.ACSmartVoucherModule__Content,
          moduleBaseStyles.Module__Wrapper,
        )}
        ref={ref}
      >
        {requiredFieldsFilled && languageNumber !== undefined ? (
          <iframe
            className={ClassNames(styles.ACSmartVoucherModule__Iframe, {
              [styles["ACSmartVoucherModule__Iframe--no-click"]]:
                !isActive && isPreview,
            })}
            id="voucher"
            src={getIframeUrl({ cssUrl, kID, languageNumber })}
            loading="lazy"
          />
        ) : (
          <Icon
            glyph="vouchers"
            className={ClassNames(
              styles.ACSmartVoucherModule__Icon,
              css({ fill: scheme.main.text }),
            )}
          />
        )}
      </div>
    </ModuleWithHeadings>
  );
};

export default ACSmartVoucherModule;
